import moment from 'moment';
import 'moment-timezone';

import * as SHIPMENT_STATUS from '../constants/shipmentStatus';

const DATE_FORMAT = 'YYYY-MM-DD';
const MAX_LOOKAHEAD_DAYS = 5;
const DEFAULT_TIMEZONE = 'America/Los_Angeles';

/**
 * The delivery window to show for a shipment: the later of its own window and the one from the
 * latest PICKUP_DELAY event, pushed to the next serviceable day. Always returns a usable window,
 * falling back to the shipment's own timestamps.
 *
 * @param {object} shipment
 * @param {Array<object>} events outbound_events, raw or translated
 * @param {Array<{ date: string, off: boolean }>} noServiceDates from the tracking detail, not the shipment
 * @returns {{ dropoff_earliest_ts: string, dropoff_latest_ts: string }}
 */
export function resolveDeliveryWindow(shipment, events, noServiceDates) {
  const shipmentWindow = {
    dropoff_earliest_ts: shipment?.dropoff_earliest_ts,
    dropoff_latest_ts: shipment?.dropoff_latest_ts,
  };

  const timezone = shipment?.timezone || DEFAULT_TIMEZONE;
  if (!moment.tz.zone(timezone)) return shipmentWindow;

  const delayWindow = parseWindow(findLatestPickupDelay(events)?.attributes, timezone);
  if (!delayWindow) return shipmentWindow;

  const candidates = [parseWindow(shipmentWindow, timezone), delayWindow && nextServiceableWindow(delayWindow, noServiceDates)].filter(Boolean);

  if (candidates.length === 0) return shipmentWindow;

  // get lastest window
  const { earliest, latest } = candidates.reduce((best, window) => (window.latest.isAfter(best.latest) ? window : best));

  return { dropoff_earliest_ts: earliest.toISOString(), dropoff_latest_ts: latest.toISOString() };
};

const findLatestPickupDelay = (events) =>
  (events || [])
    .filter(
      (event) => event?.signal === SHIPMENT_STATUS.PICKUP_DELAY && event?.attributes?.dropoff_earliest_ts && event?.attributes?.dropoff_latest_ts,
    )
    .sort((a, b) => (a.ts && b.ts) ? new Date(b.ts).getTime() - new Date(a.ts).getTime() : 0)[0];

/** Get next serviceable window */
const nextServiceableWindow = ({ earliest, latest }, noServiceDates) => {
  const blockedDates = new Set((noServiceDates || []).filter((item) => item?.off === true).map((item) => item.date));

  // clone once: moment mutates in place, and the caller still needs the original window
  const nextEarliest = earliest.clone();
  const nextLatest = latest.clone();

  for (let attempt = 1; attempt <= MAX_LOOKAHEAD_DAYS; attempt += 1) {
    nextEarliest.add(1, 'days');
    nextLatest.add(1, 'days');

    if (!blockedDates.has(nextLatest.format(DATE_FORMAT))) {
      return { earliest: nextEarliest, latest: nextLatest };
    }
  }

  return null;
};

/**
 * Both ends as moments in `timezone`, or null if either end is missing or unparseable.
 * Timestamps are ISO 8601 strings or epoch milliseconds as a number.
 */
const parseWindow = (window, timezone) => {
  const { dropoff_earliest_ts: earliest, dropoff_latest_ts: latest } = window || {};
  if (!earliest || !latest) return null;

  const parsed = { earliest: moment.tz(earliest, timezone), latest: moment.tz(latest, timezone) };

  return parsed.earliest.isValid() && parsed.latest.isValid() ? parsed : null;
};